/**
 * PdfGateModal — PDF page range selector shown before uploading large PDFs (> 100 pages).
 *
 * Triggered by useFileUpload when page count > 100. Shows:
 * - Tier cap warning (if PDF exceeds plan limit) with multi-upload hint
 * - Credit estimate for the selected range
 * - Low-balance warning with Top Up link (non-blocking)
 * - Page range inputs (From / To) with live cost calculation
 * - Quick presets based on tier cap
 *
 * Tier caps: Free=200, Trial/Pro=500, Max=unlimited.
 * First 100 pages are always free; cost = max(0, rangeSize - 100) credits.
 *
 * Mounted in GlobalUploadModal.jsx, receives callbacks from useFileUpload.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@legendapp/state/react';
import { AlertTriangle, Zap, FileText } from 'lucide-react';
import { Dialog, Button, ICON_SIZE } from '../design-system';
import { ui$, openUsageModal } from '../store/ui$';
import { trackEvent } from '../lib/analytics';
import './PdfGateModal.css';

function PdfGateModal({ onConfirm, onCancel }) {
  const { t } = useTranslation('articles');
  const modalData = useSelector(() => ui$.modalData.get());

  const {
    fileName = 'document',
    numPages = 0,
    tierCap = 200,
    creditBalance = 0,
    initialFrom = 1,
    initialTo,
  } = modalData || {};

  // tierCap is a range-SIZE limit, not an absolute page-number limit.
  // effectiveMax = the highest "To" value allowed given the current fromPage.
  const calcEffectiveMax = (from) =>
    tierCap === Infinity ? numPages : Math.min(from + tierCap - 1, numPages);

  // State must be declared BEFORE any derived values that reference it (TDZ).
  const [fromPage, setFromPage] = useState(initialFrom);
  const defaultTo = initialTo ?? calcEffectiveMax(initialFrom);
  const [toPage, setToPage] = useState(defaultTo);

  const effectiveMax = calcEffectiveMax(fromPage);

  // Reset when modal data changes (e.g. re-opened for next segment)
  useEffect(() => {
    setFromPage(initialFrom);
    setToPage(initialTo ?? calcEffectiveMax(initialFrom));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileName, initialFrom, initialTo]);

  const rangeSize = Math.max(0, toPage - fromPage + 1);
  const estimatedCredits = Math.max(0, rangeSize - 100);
  const isFree = estimatedCredits === 0;
  const hasEnoughCredits = creditBalance >= estimatedCredits;
  // Banner: is the total document larger than one upload can handle?
  const exceedsCap = tierCap !== Infinity && numPages > tierCap;
  const isValid = fromPage >= 1 && toPage >= fromPage && toPage <= effectiveMax;

  const handleFromChange = (e) => {
    const val = parseInt(e.target.value, 10);
    if (isNaN(val)) return;
    const newFrom = Math.max(1, Math.min(val, numPages));
    const newMax = calcEffectiveMax(newFrom);
    setFromPage(newFrom);
    // Re-clamp toPage if it now exceeds the new effectiveMax
    if (toPage > newMax) setToPage(newMax);
  };

  const handleToChange = (e) => {
    const val = parseInt(e.target.value, 10);
    if (isNaN(val)) return;
    const clamped = Math.max(fromPage, Math.min(val, effectiveMax));
    setToPage(clamped);
  };

  const handlePreset = (from, to) => {
    setFromPage(from);
    setToPage(Math.min(to, calcEffectiveMax(from)));
  };

  // Tier display name + upgrade CTA text (declared before handlers that reference them)
  const tierName = tierCap === Infinity ? 'Max' : tierCap >= 500 ? 'Pro' : 'Free';
  const upgradeLabel = tierName === 'Free'
    ? t('pdfGate.upgradeToPro', { defaultValue: 'Upgrade to Pro · 500 pages' })
    : t('pdfGate.upgradeToMax', { defaultValue: 'Upgrade to Max · Unlimited' });

  const handleConfirm = useCallback(() => {
    if (!isValid) return;
    trackEvent('pdf_gate_confirmed', {
      num_pages: numPages,
      tier: tierName,
      from_page: fromPage,
      to_page: toPage,
      range_size: rangeSize,
      estimated_credits: estimatedCredits,
      is_free: isFree,
      is_continuation: initialFrom > 1,
    });
    onConfirm(fromPage, toPage);
  }, [isValid, fromPage, toPage, onConfirm, numPages, tierName, rangeSize, estimatedCredits, isFree, initialFrom]);

  const handleCancel = useCallback(() => {
    trackEvent('pdf_gate_cancelled', {
      num_pages: numPages,
      tier: tierName,
      range_size: rangeSize,
    });
    onCancel();
  }, [onCancel, numPages, tierName, rangeSize]);

  const handleUpgradeClick = () => {
    trackEvent('upgrade_prompt_cta_clicked', { feature: 'pdf_gate', from_tier: tierName });
    openUsageModal('plan', 'pdf_upload');
  };

  // Fire once per gate opening — component mounts/unmounts per open
  useEffect(() => {
    trackEvent('feature_gate_hit', {
      feature: 'pdf_pages',
      tier: tierName,
      num_pages: numPages,
      tier_cap: tierCap === Infinity ? null : tierCap,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Presets start from fromPage (correct for any segment, including continuations).
  // segmentTierLimited: the tier cap — not the document end — limits this segment's max.
  const segmentTierLimited = tierCap !== Infinity && (fromPage + tierCap - 1) < numPages;
  const rangeAvail = effectiveMax - fromPage + 1;
  const presets = [];
  if (rangeAvail > 50) {
    presets.push({ label: `${fromPage}–${fromPage + 49}`, from: fromPage, to: fromPage + 49 });
  }
  if (rangeAvail > 100) {
    presets.push({ label: `${fromPage}–${fromPage + 99}`, from: fromPage, to: fromPage + 99 });
  }
  presets.push({
    label: segmentTierLimited
      ? t('pdfGate.presetPlanMax', { limit: tierCap, defaultValue: `${fromPage}–${effectiveMax} (plan max)` })
      : fromPage === 1
        ? t('pdfGate.presetAll', { pages: numPages, defaultValue: `All ${numPages} pages` })
        : `${fromPage}–${effectiveMax}`,
    from: fromPage,
    to: effectiveMax,
  });

  const isPresetActive = (p) => fromPage === p.from && toPage === p.to;

  return (
    <Dialog
      isOpen={true}
      onClose={handleCancel}
      title={
        <span className="pdf-gate-title">
          <FileText size={ICON_SIZE.sm} />
          <span className="pdf-gate-filename" title={fileName}>{fileName}</span>
          <span className="pdf-gate-page-badge">
            {numPages} {t('pdfGate.pages', { defaultValue: 'pages' })}
          </span>
        </span>
      }
      size="sm"
      footer={
        <>
          <Button variant="neutral" onClick={handleCancel}>
            {t('common:cancel', 'Cancel')}
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={!isValid}>
            {t('pdfGate.confirm', {
              from: fromPage,
              to: toPage,
              defaultValue: `Process pages ${fromPage}–${toPage}`,
            })}
          </Button>
        </>
      }
    >
      {exceedsCap && (
        <div className="pdf-gate-banner pdf-gate-banner--tier">
          <AlertTriangle size={ICON_SIZE.xs} />
          <div>
            <strong>
              {t('pdfGate.tierCapTitle', {
                tier: tierName,
                limit: tierCap,
                defaultValue: `${tierName} plan: up to ${tierCap} pages per upload`,
              })}
            </strong>
            <p>
              {t('pdfGate.multipleUploadsHint', {
                defaultValue: 'Upload different page ranges separately to process the full document.',
              })}
            </p>
            <button
              className="pdf-gate-upgrade-btn"
              onClick={handleUpgradeClick}
            >
              {upgradeLabel}
            </button>
          </div>
        </div>
      )}

      {!isFree && !hasEnoughCredits && (
        <div className="pdf-gate-banner pdf-gate-banner--credits">
          <Zap size={ICON_SIZE.xs} />
          <span>
            {t('pdfGate.lowBalance', {
              needed: estimatedCredits,
              have: Math.max(0, Math.round(creditBalance)),
              defaultValue: `~${estimatedCredits} credits needed · You have: ${Math.max(0, Math.round(creditBalance))}`,
            })}
            {' '}
            <button
              className="pdf-gate-topup-link"
              onClick={() => openUsageModal('balance', 'pdf_processing')}
            >
              {t('pdfGate.topUp', { defaultValue: 'Top up ↗' })}
            </button>
          </span>
        </div>
      )}

      <div className="pdf-gate-range">
        <div className="pdf-gate-range-label">
          {t('pdfGate.selectRange', { defaultValue: 'Pages to process' })}
        </div>

        <div className="pdf-gate-range-inputs">
          <div className="pdf-gate-input-group">
            <label htmlFor="pdf-gate-from">{t('pdfGate.from', { defaultValue: 'From' })}</label>
            <input
              id="pdf-gate-from"
              type="number"
              value={fromPage}
              onChange={handleFromChange}
              min={1}
              max={toPage}
            />
          </div>
          <span className="pdf-gate-dash">–</span>
          <div className="pdf-gate-input-group">
            <label htmlFor="pdf-gate-to">{t('pdfGate.to', { defaultValue: 'To' })}</label>
            <input
              id="pdf-gate-to"
              type="number"
              value={toPage}
              onChange={handleToChange}
              min={fromPage}
              max={effectiveMax}
            />
          </div>
        </div>

        <div className="pdf-gate-presets">
          {presets.map((p) => (
            <button
              key={p.label}
              className={`pdf-gate-preset${isPresetActive(p) ? ' pdf-gate-preset--active' : ''}`}
              onClick={() => handlePreset(p.from, p.to)}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className={`pdf-gate-cost${isFree ? ' pdf-gate-cost--free' : hasEnoughCredits ? ' pdf-gate-cost--ok' : ' pdf-gate-cost--low'}`}>
          {isFree
            ? t('pdfGate.free', { defaultValue: '✓ Included · first 100 pages free' })
            : `~${estimatedCredits} ${t('pdfGate.credits', { defaultValue: 'credits' })} ${hasEnoughCredits ? '✓' : '⚠'}`
          }
        </div>
      </div>
    </Dialog>
  );
}

export default PdfGateModal;
